<template>
  <div class="order-details">
    <Button
      label="Назад"
      icon="pi pi-arrow-left"
      class="back-btn"
      outlined
      @click="goBack"
    />

    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Загрузка заказа...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <Button
        label="Повторить"
        icon="pi pi-refresh"
        class="retry-btn"
        @click="loadOrder"
      />
    </div>

    <div v-else-if="ordersStore.currentOrder" class="order-content">
      <div class="order-header">
        <div>
          <h1>Заказ #{{ ordersStore.currentOrder.id }}</h1>
          <p v-if="canShowInternalPricing" class="order-subtitle">
            Внутренние B2B-данные показывают закупочную цену, максимальную
            скидку и маржу по заказу.
          </p>
        </div>

        <div class="total-amount">
          <span class="label">Итого для клиента:</span>
          <span class="amount">{{ formatPrice(displayedOrderTotal) }}</span>
        </div>
      </div>

      <div class="order-info-card">
        <h3>Информация о заказе</h3>

        <div class="info-row">
          <span class="label">Номер заказа:</span>
          <span class="value">#{{ ordersStore.currentOrder.id }}</span>
        </div>

        <div class="info-row">
          <span class="label">ID клиента:</span>
          <span class="value">{{ ordersStore.currentOrder.clientId }}</span>
        </div>

        <div class="info-row">
          <span class="label">Имя клиента:</span>
          <span class="value">{{
            ordersStore.currentOrder.clientName || "Не указано"
          }}</span>
        </div>

        <div class="info-row">
          <span class="label">Email клиента:</span>
          <span class="value">{{
            ordersStore.currentOrder.clientEmail || "Не указан"
          }}</span>
        </div>

        <div class="info-row">
          <span class="label">Телефон клиента:</span>
          <span class="value">{{
            ordersStore.currentOrder.clientPhone || "Не указан"
          }}</span>
        </div>

        <div class="info-row">
          <span class="label">Дата создания:</span>
          <span class="value">{{
            formatFullDate(ordersStore.currentOrder.createdAt)
          }}</span>
        </div>

        <div class="info-row">
          <span class="label">Статус:</span>
          <span class="value status-value">
            <Dropdown
              v-if="canChangeOrderStatus"
              :model-value="currentOrderStatus"
              :options="ORDER_STATUSES"
              class="status-dropdown"
              :disabled="isStatusSaving"
              @change="handleStatusChange($event.value)"
            />
            <span
              v-else
              class="status-chip"
              :class="getStatusClass(currentOrderStatus)"
            >
              {{ currentOrderStatus }}
            </span>
          </span>
        </div>

        <div class="info-row">
          <span class="label">Количество позиций:</span>
          <span class="value"
            >{{ ordersStore.currentOrder.items?.length || 0 }} шт.</span
          >
        </div>

        <div v-if="canShowInternalPricing" class="external-offer-client-card">
          <div>
            <span>Клиент для внешнего КП</span>
            <strong>{{
              currentExternalOfferClientInfo?.clientName || "Не указан"
            }}</strong>
            <small v-if="currentExternalOfferClientInfo?.clientPhone">
              {{ currentExternalOfferClientInfo.clientPhone }}
            </small>
          </div>

          <Button
            :label="currentExternalOfferClientInfo ? 'Изменить' : 'Указать'"
            size="small"
            text
            @click="openExternalCommercialOfferDialog()"
          />
        </div>

        <div class="order-actions">
          <Button
            label="Сформировать КП"
            icon="pi pi-file-pdf"
            class="offer-btn"
            severity="secondary"
            outlined
            :loading="isGeneratingCommercialOffer"
            :disabled="!Number.isInteger(orderId) || orderId <= 0"
            @click="handleExternalCommercialOfferButtonClick"
          />

          <Button
            v-if="canGenerateInternalCommercialOffer"
            label="Внутреннее КП"
            icon="pi pi-briefcase"
            class="internal-offer-btn"
            severity="secondary"
            :loading="isGeneratingInternalCommercialOffer"
            :disabled="!Number.isInteger(orderId) || orderId <= 0"
            @click="handleGenerateInternalCommercialOffer"
          />

          <Button
            v-if="
              canAssignOrder &&
              ordersStore.currentOrder?.status === 'Новая' &&
              ordersStore.currentOrder?.assignedUserId !== authStore.user?.id
            "
            label="Взять в работу"
            icon="pi pi-user-plus"
            class="take-work-btn"
            severity="primary"
            :loading="isAssigning"
            :disabled="
              !Number.isInteger(orderId) || orderId <= 0 || isAssigning
            "
            @click="handleAssignOrder"
          />
        </div>
      </div>

      <div class="order-items">
        <h2>Товары в заказе</h2>

        <div class="items-list">
          <div
            v-for="item in ordersStore.currentOrder.items"
            :key="item.id"
            class="item-card"
          >
            <div class="item-info">
              <h3>{{ item.productHistory?.name || "Товар" }}</h3>
              <p class="item-articul">
                Артикул: {{ item.productHistory?.articul || "—" }}
              </p>

              <div class="item-price-info">
                <span>Количество: {{ item.quantity }} шт.</span>
                <span
                  >Цена клиенту: {{ formatPrice(getClientUnitPrice(item)) }} /
                  шт.</span
                >
              </div>

              <div v-if="canShowInternalPricing" class="item-internal-grid">
                <div class="internal-cell">
                  <span class="internal-label">Базовая цена</span>
                  <strong>{{ formatPrice(getBaseUnitPrice(item)) }}</strong>
                </div>

                <div class="internal-cell">
                  <span class="internal-label">Max скидка B2B</span>
                  <strong>{{ formatPercent(item.maxDiscountPercent) }}</strong>
                </div>

                <div class="internal-cell">
                  <span class="internal-label">Скидка клиенту</span>
                  <strong>{{
                    formatPercent(item.selectedDiscountPercent)
                  }}</strong>
                </div>

                <div class="internal-cell">
                  <span class="internal-label">Закупка B2B</span>
                  <strong>{{ formatPrice(getPurchaseUnitPrice(item)) }}</strong>
                </div>

                <div class="internal-cell">
                  <span class="internal-label">Маржа / шт.</span>
                  <strong>{{ formatPrice(getMarginPerUnit(item)) }}</strong>
                </div>

                <div class="internal-cell">
                  <span class="internal-label">Маржа всего</span>
                  <strong>{{ formatPrice(getMarginTotal(item)) }}</strong>
                </div>
              </div>

              <div class="item-total">
                <span>Сумма:</span>
                <strong>{{ formatPrice(getLineTotal(item)) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="order-summary">
        <div class="summary-row">
          <span>Итого для клиента:</span>
          <span>{{ formatPrice(displayedOrderTotal) }}</span>
        </div>

        <div v-if="canShowInternalPricing" class="summary-row">
          <span>Закупка B2B:</span>
          <span>{{
            formatPrice(getPurchaseOrderTotal(ordersStore.currentOrder.items))
          }}</span>
        </div>

        <div v-if="canShowInternalPricing" class="summary-row">
          <span>Маржа:</span>
          <span>{{
            formatPrice(getMarginOrderTotal(ordersStore.currentOrder.items))
          }}</span>
        </div>

        <div class="summary-row grand-total">
          <span>{{
            canShowInternalPricing
              ? "Клиентский итог заказа:"
              : "Итого к оплате:"
          }}</span>
          <span>{{ formatPrice(displayedOrderTotal) }}</span>
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
      <p>Нет данных о заказе</p>
    </div>

    <Dialog
      v-model:visible="externalOfferDialogVisible"
      @hide="resetExternalOfferClientDraft"
      header="Данные клиента для КП"
      :modal="true"
      :style="{ width: '420px' }"
    >
      <div class="external-offer-dialog">
        <div class="field">
          <label>Имя клиента</label>
          <InputText
            v-model.trim="externalOfferClientName"
            placeholder="Например, Иван Петров"
            class="w-full"
            autofocus
            @keyup.enter="confirmGenerateCommercialOffer"
          />
        </div>

        <div class="field">
          <label>Телефон клиента</label>
          <InputText
            v-model="externalOfferClientPhone"
            class="w-full"
            @input="handleExternalOfferPhoneInput"
            @keyup.enter="confirmGenerateCommercialOffer"
          />
        </div>

        <small v-if="externalOfferClientError" class="field-error">
          {{ externalOfferClientError }}
        </small>

        <div class="dialog-footer">
          <Button
            label="Отмена"
            text
            @click="externalOfferDialogVisible = false"
          />
          <Button
            :label="
              externalOfferGenerateAfterSave
                ? 'Сохранить и сформировать КП'
                : 'Сохранить'
            "
            severity="primary"
            :loading="isGeneratingCommercialOffer"
            @click="confirmGenerateCommercialOffer"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import { useAppToast } from "@/shared/lib/useAppToast";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import {
  generateInternalOrderCommercialOfferPdf,
  generateOrderCommercialOfferPdf,
} from "@/modules/cart/api/commercialOffer.service";
import {
  loadCommercialOfferClientInfo,
  saveCommercialOfferClientInfo,
  validateCommercialOfferClientInfo,
} from "@/modules/cart/lib/commercialOfferClientInfo";
import { ORDER_STATUSES } from "../api/orders.service";
import { useOrdersStore } from "../store/orders.store";
import { usePermissionsStore } from "@/modules/auth/store/permissions.store";
import { assignOrder } from "@/modules/orders/api/orders.service";

const route = useRoute();
const router = useRouter();
const toast = useAppToast();
const authStore = useAuthStore();
const ordersStore = useOrdersStore();
const permissionsStore = usePermissionsStore();
const { can } = usePermissions();

const orderId = computed(() => parseInt(route.params.id, 10));
const isLoading = computed(() => ordersStore.isLoading);
const error = computed(() => ordersStore.error);
const isGeneratingCommercialOffer = ref(false);
const isGeneratingInternalCommercialOffer = ref(false);
const externalOfferDialogVisible = ref(false);
const externalOfferClientName = ref("");
const externalOfferClientPhone = ref("");
const externalOfferClientError = ref("");
const externalOfferGenerateAfterSave = ref(false);
const currentExternalOfferClientInfo = ref(null);
const isStatusSaving = ref(false);
const canAssignOrder = computed(() =>
  permissionsStore.hasAnyPermission(["AssignOrder"]),
);

const isAssigning = ref(false);

const handleAssignOrder = async () => {
  if (!orderId.value || isAssigning.value) return;

  isAssigning.value = true;

  try {
    const response = await assignOrder(orderId.value);
    if (response.status === 200) {
      toast.success(`Заказ №${orderId.value} назначен на вас`);
      await loadOrder();
    }
  } catch (error) {
    console.error("Ошибка при назначении заказа:", error);
    toast.error(error.response?.data?.message || "Не удалось назначить заказ");
  } finally {
    isAssigning.value = false;
  }
};
const currentOrderStatus = computed(() => {
  return ordersStore.currentOrder?.status || ORDER_STATUSES[0];
});

const canChangeOrderStatus = computed(() => can("ChangeOrderStatus"));

const canShowInternalPricing = computed(() => {
  const currentUser = authStore.user;
  const currentOrder = ordersStore.currentOrder;

  return (
    currentUser?.clientType === "B2B" &&
    currentOrder?.clientId === currentUser?.id
  );
});

const canGenerateInternalCommercialOffer = computed(
  () => canShowInternalPricing.value || canChangeOrderStatus.value,
);

const loadCurrentExternalOfferClientInfo = () => {
  currentExternalOfferClientInfo.value = loadCommercialOfferClientInfo(
    "order",
    authStore.user?.id,
    orderId.value,
  );
};

const getCurrentExternalOfferClientInfo = () => {
  loadCurrentExternalOfferClientInfo();
  return currentExternalOfferClientInfo.value;
};

const saveCurrentExternalOfferClientInfo = (clientInfo) => {
  const result = saveCommercialOfferClientInfo(
    "order",
    authStore.user?.id,
    orderId.value,
    clientInfo,
  );

  if (result.isValid) {
    currentExternalOfferClientInfo.value = result.value;
  }

  return result;
};

const setExternalOfferClientPhone = (value) => {
  externalOfferClientPhone.value = value;
  externalOfferClientError.value = "";
};

const handleExternalOfferPhoneInput = (event) => {
  setExternalOfferClientPhone(event?.target?.value ?? "");
};

const normalizeAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const roundAmount = (value) => Math.round(normalizeAmount(value) * 100) / 100;
const normalizeDiscountPercent = (value) => {
  const amount = Math.floor(normalizeAmount(value));
  return Math.min(Math.max(amount, 0), 100);
};

const applyDiscountToBase = (baseUnitPrice, discountPercent) => {
  const normalizedBasePrice = roundAmount(baseUnitPrice);

  if (normalizedBasePrice <= 0) {
    return 0;
  }

  const normalizedDiscount = normalizeDiscountPercent(discountPercent);
  return roundAmount(
    normalizedBasePrice - (normalizedBasePrice * normalizedDiscount) / 100,
  );
};

const formatFullDate = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleString("ru-RU", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPrice = (price) => {
  return new Intl.NumberFormat("ru-KZ", {
    style: "currency",
    currency: "KZT",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(normalizeAmount(price));
};

const formatPercent = (value) => {
  const amount = normalizeAmount(value);

  return `${amount.toLocaleString("ru-RU", {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  })}%`;
};

const getStatusClass = (status) => {
  const normalizedStatus = status || ORDER_STATUSES[0];

  return {
    "status-new": normalizedStatus === "Новая",
    "status-progress": normalizedStatus === "В работе",
    "status-delivery": normalizedStatus === "Доставка",
    "status-paid": normalizedStatus === "Оплачено/Закрыта",
    "status-unpaid": normalizedStatus === "Неоплачена/Закрыта",
  };
};

const handleStatusChange = async (status) => {
  if (!status || status === currentOrderStatus.value || isStatusSaving.value) {
    return;
  }

  isStatusSaving.value = true;

  try {
    await ordersStore.updateOrderStatus(orderId.value, status);
    toast.success("Статус заказа обновлён");
  } catch {
    toast.error("Не удалось обновить статус заказа");
  } finally {
    isStatusSaving.value = false;
  }
};

const getBaseUnitPrice = (item) => {
  const snapshotBasePrice = normalizeAmount(item?.productHistory?.basePrice);

  if (snapshotBasePrice > 0) {
    return roundAmount(snapshotBasePrice);
  }

  const quantity = Math.max(Number(item?.quantity) || 0, 0);
  const fallbackLineTotal = roundAmount(item?.price);

  if (quantity <= 0) {
    return fallbackLineTotal;
  }

  const clientUnitPrice = roundAmount(fallbackLineTotal / quantity);
  const selectedDiscountPercent = normalizeDiscountPercent(
    item?.selectedDiscountPercent,
  );

  if (selectedDiscountPercent <= 0 || selectedDiscountPercent >= 100) {
    return clientUnitPrice;
  }

  return roundAmount(clientUnitPrice / (1 - selectedDiscountPercent / 100));
};

const getResolvedSelectedDiscountPercent = (item) => {
  const maxDiscountPercent = normalizeDiscountPercent(item?.maxDiscountPercent);
  const selectedDiscountPercent = normalizeDiscountPercent(
    item?.selectedDiscountPercent,
  );

  return maxDiscountPercent > 0
    ? Math.min(selectedDiscountPercent, maxDiscountPercent)
    : selectedDiscountPercent;
};

const shouldUseDerivedPricing = (item) => {
  return (
    getBaseUnitPrice(item) > 0 &&
    (normalizeDiscountPercent(item?.maxDiscountPercent) > 0 ||
      normalizeDiscountPercent(item?.selectedDiscountPercent) > 0)
  );
};

const getClientUnitPrice = (item) => {
  if (shouldUseDerivedPricing(item)) {
    return applyDiscountToBase(
      getBaseUnitPrice(item),
      getResolvedSelectedDiscountPercent(item),
    );
  }

  const quantity = Math.max(Number(item?.quantity) || 0, 0);

  if (quantity === 0) {
    return roundAmount(item?.price);
  }

  return roundAmount(normalizeAmount(item?.price) / quantity);
};

const getLineTotal = (item) => {
  if (shouldUseDerivedPricing(item)) {
    return roundAmount(
      getClientUnitPrice(item) * Math.max(Number(item?.quantity) || 0, 0),
    );
  }

  return roundAmount(item?.price);
};

const getPurchaseUnitPrice = (item) => {
  const baseUnitPrice = getBaseUnitPrice(item);
  const maxDiscountPercent = normalizeDiscountPercent(item?.maxDiscountPercent);

  if (baseUnitPrice > 0 && maxDiscountPercent > 0) {
    return applyDiscountToBase(baseUnitPrice, maxDiscountPercent);
  }

  const purchasePrice = normalizeAmount(item?.purchaseUnitPrice);
  return purchasePrice > 0
    ? roundAmount(purchasePrice)
    : getClientUnitPrice(item);
};

const getPurchaseTotal = (item) => {
  return roundAmount(
    getPurchaseUnitPrice(item) * Math.max(Number(item?.quantity) || 0, 0),
  );
};

const getMarginPerUnit = (item) => {
  return roundAmount(getClientUnitPrice(item) - getPurchaseUnitPrice(item));
};

const getMarginTotal = (item) => {
  return roundAmount(getLineTotal(item) - getPurchaseTotal(item));
};

const getPurchaseOrderTotal = (items) => {
  return roundAmount(
    (items || []).reduce((sum, item) => sum + getPurchaseTotal(item), 0),
  );
};

const getMarginOrderTotal = (items) => {
  return roundAmount(
    (items || []).reduce((sum, item) => sum + getMarginTotal(item), 0),
  );
};

const displayedOrderTotal = computed(() => {
  return roundAmount(
    (ordersStore.currentOrder?.items || []).reduce(
      (sum, item) => sum + getLineTotal(item),
      0,
    ),
  );
});

const getFileNameFromHeaders = (headers) => {
  const contentDisposition = headers?.["content-disposition"];

  if (!contentDisposition) {
    return null;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const fallbackMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  return fallbackMatch?.[1] ?? null;
};

const downloadFile = (blob, fileName) => {
  const fileUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = fileUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(fileUrl);
};

const extractCommercialOfferError = async (errorValue) => {
  const responseData = errorValue?.response?.data;

  if (responseData instanceof Blob) {
    const text = await responseData.text();

    if (!text) {
      return "Не удалось сформировать КП";
    }

    try {
      const payload = JSON.parse(text);
      return (
        payload?.errors?.[0]?.errors?.[0] ||
        payload?.detail ||
        payload?.title ||
        "Не удалось сформировать КП"
      );
    } catch {
      return text;
    }
  }

  return (
    responseData?.errors?.[0]?.errors?.[0] ||
    responseData?.detail ||
    errorValue?.message ||
    "Не удалось сформировать КП"
  );
};

const loadOrder = async () => {
  if (!Number.isInteger(orderId.value) || orderId.value <= 0) {
    toast.error("Некорректный идентификатор заказа");
    return;
  }

  try {
    await ordersStore.fetchOrderById(orderId.value);
  } catch (e) {
    toast.error(e?.message || "Не удалось загрузить заказ");
  }
};

const goBack = () => {
  if (route.path.startsWith("/admin")) {
    router.push("/admin/orders");
    return;
  }

  router.push("/orders");
};

const downloadCommercialOffer = async ({
  request,
  fallbackFileName,
  loadingRef,
  successSummary,
}) => {
  if (
    !Number.isInteger(orderId.value) ||
    orderId.value <= 0 ||
    loadingRef.value
  ) {
    return;
  }

  loadingRef.value = true;

  try {
    const response = await request(orderId.value);
    const fileName =
      getFileNameFromHeaders(response.headers) ||
      fallbackFileName(orderId.value);

    downloadFile(response.data, fileName);
    toast.success("PDF успешно скачан", successSummary);
  } catch (e) {
    const detail = await extractCommercialOfferError(e);
    toast.error(detail);
  } finally {
    loadingRef.value = false;
  }
};

const handleExternalCommercialOfferButtonClick = async () => {
  if (!canShowInternalPricing.value) {
    await handleGenerateCommercialOffer();
    return;
  }

  const savedClientInfo = getCurrentExternalOfferClientInfo();

  if (savedClientInfo) {
    await handleGenerateCommercialOffer(savedClientInfo);
    return;
  }

  openExternalCommercialOfferDialog(true);
};

const resetExternalOfferClientDraft = () => {
  externalOfferClientName.value = "";
  externalOfferClientPhone.value = "";
  externalOfferClientError.value = "";
};

const openExternalCommercialOfferDialog = (generateAfterSave = false) => {
  const savedClientInfo = getCurrentExternalOfferClientInfo();

  externalOfferGenerateAfterSave.value = generateAfterSave;
  externalOfferClientName.value = savedClientInfo?.clientName || "";
  externalOfferClientPhone.value = savedClientInfo?.clientPhone || "";
  externalOfferClientError.value = "";
  externalOfferDialogVisible.value = true;
};

const confirmGenerateCommercialOffer = async () => {
  const validation = validateCommercialOfferClientInfo({
    clientName: externalOfferClientName.value,
    clientPhone: externalOfferClientPhone.value,
  });

  if (!validation.isValid) {
    externalOfferClientError.value = validation.message;
    return;
  }

  const saveResult = saveCurrentExternalOfferClientInfo(validation.value);

  if (!saveResult.isValid) {
    externalOfferClientError.value = saveResult.message;
    return;
  }

  externalOfferDialogVisible.value = false;

  if (externalOfferGenerateAfterSave.value) {
    await handleGenerateCommercialOffer(saveResult.value);
    return;
  }

  toast.success(
    "Теперь внешнее КП будет формироваться с этими данными",
    "Данные клиента сохранены",
  );
};

const handleGenerateCommercialOffer = async (options = {}) => {
  await downloadCommercialOffer({
    request: (id) => generateOrderCommercialOfferPdf(id, options),
    fallbackFileName: (id) => `commercial-offer-order-${id}.pdf`,
    loadingRef: isGeneratingCommercialOffer,
    successSummary: "КП сформировано",
  });
};

const handleGenerateInternalCommercialOffer = async () => {
  if (!canGenerateInternalCommercialOffer.value) {
    return;
  }

  await downloadCommercialOffer({
    request: generateInternalOrderCommercialOfferPdf,
    fallbackFileName: (id) => `internal-commercial-offer-order-${id}.pdf`,
    loadingRef: isGeneratingInternalCommercialOffer,
    successSummary: "Внутреннее КП сформировано",
  });
};

watch(
  () => error.value,
  (newError) => {
    if (newError) {
      toast.error(newError);
    }
  },
);

watch(
  () => [authStore.user?.id, orderId.value],
  () => {
    loadCurrentExternalOfferClientInfo();
  },
  { immediate: true },
);

onMounted(() => {
  loadOrder();
});

onUnmounted(() => {
  ordersStore.clearCurrentOrder();
});
</script>

<style scoped>
:deep(.take-work-btn.p-button) {
  background: #c41217;
  border: none;
  color: white;
  border-radius: 12px;
  box-shadow: none;
  font-weight: 600;
}

:deep(.take-work-btn.p-button:hover) {
  background: #a00e12;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(196, 18, 23, 0.3);
}

:deep(.take-work-btn.p-button:disabled) {
  background: #e0e0e0;
  border: none;
  color: #999;
  transform: none;
  box-shadow: none;
}
.order-details {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
}

:deep(.back-btn.p-button) {
  margin-bottom: 24px;
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.back-btn.p-button .p-button-icon) {
  color: #c41217;
}

.order-header {
  background: white;
  padding: 24px 32px;
  border-radius: 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid #f0f0f0;
}

.order-header h1 {
  margin: 0;
  color: #1d1d1d;
  font-size: 26px;
}

.order-subtitle {
  margin: 10px 0 0;
  color: #6c757d;
  font-size: 14px;
  max-width: 560px;
}

.total-amount {
  text-align: right;
}

.total-amount .label {
  display: block;
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 6px;
}

.total-amount .amount {
  font-size: 32px;
  font-weight: 700;
  color: #c41217;
}

.order-info-card,
.order-items,
.order-summary {
  background: white;
  padding: 24px;
  border-radius: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid #f0f0f0;
}

.order-info-card h3,
.order-items h2 {
  margin: 0 0 18px 0;
  color: #1d1d1d;
}

.order-actions {
  padding-top: 18px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.external-offer-client-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px;
  margin-top: 16px;
  border: 1px solid #ececec;
  border-radius: 14px;
  background: #fafafa;
}

.external-offer-client-card div {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.external-offer-client-card span,
.external-offer-client-card small {
  color: #6c757d;
  font-size: 12px;
}

.external-offer-client-card strong {
  color: #1d1d1d;
  font-size: 14px;
  overflow-wrap: anywhere;
}

:deep(.offer-btn.p-button),
:deep(.internal-offer-btn.p-button) {
  width: 100%;
  border-radius: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
}

.info-row:last-of-type {
  border-bottom: none;
}

.info-row .label {
  color: #6c757d;
}

.info-row .value {
  font-weight: 500;
  color: #1d1d1d;
  text-align: right;
}

.status-value {
  display: inline-flex;
  justify-content: flex-end;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.status-new {
  background: #eef2ff;
  color: #3730a3;
}

.status-progress {
  background: #fff7ed;
  color: #9a3412;
}

.status-delivery {
  background: #ecfeff;
  color: #0e7490;
}

.status-paid {
  background: #ecfdf5;
  color: #047857;
}

.status-unpaid {
  background: #fef2f2;
  color: #b91c1c;
}

:deep(.status-dropdown.p-dropdown) {
  min-width: 180px;
  border-radius: 10px;
  border-color: #e5e7eb;
  box-shadow: none;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.item-card {
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 16px;
  transition: all 0.2s;
}

.item-card:hover {
  background: #fafafa;
  border-color: #e0e0e0;
}

.item-info h3 {
  margin: 0 0 8px 0;
  color: #1d1d1d;
  font-size: 16px;
}

.item-articul {
  font-size: 12px;
  color: #6c757d;
  margin: 0 0 12px 0;
}

.item-price-info {
  display: flex;
  gap: 24px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #6c757d;
  flex-wrap: wrap;
}

.item-internal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding: 14px;
  margin-bottom: 14px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid #ececec;
}

.internal-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.internal-label {
  font-size: 12px;
  color: #6c757d;
}

.internal-cell strong {
  color: #1d1d1d;
  font-size: 14px;
}

.item-total,
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.item-total {
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.item-total strong {
  font-size: 18px;
  color: #c41217;
}

.summary-row {
  padding: 12px 0;
  font-size: 16px;
}

.grand-total {
  font-size: 20px;
  font-weight: 700;
  border-top: 2px solid #f0f0f0;
  margin-top: 12px;
  padding-top: 18px;
}

.grand-total span:last-child {
  color: #c41217;
}

.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: rgb(255, 255, 255);
  border-radius: 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #c41217;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.error-message {
  color: #dc3545;
  margin-bottom: 20px;
}

:deep(.retry-btn.p-button) {
  padding: 12px 24px;
  background: #c41217;
  border: none;
  color: white;
  border-radius: 10px;
  box-shadow: none;
  font-weight: 600;
}

:deep(.retry-btn.p-button:hover) {
  background: #a00e12;
}

.external-offer-dialog,
.field {
  display: flex;
  flex-direction: column;
}

.external-offer-dialog {
  gap: 14px;
}

.field {
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: #1d1d1d;
}

.w-full {
  width: 100%;
}

.field-error {
  color: #dc3545;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

@media (max-width: 768px) {
  .order-details {
    padding: 16px;
  }

  .order-header,
  .order-info-card,
  .order-items,
  .order-summary {
    padding: 18px;
  }

  .item-internal-grid {
    grid-template-columns: 1fr;
  }

  .info-row,
  .item-total,
  .summary-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-row .value {
    text-align: left;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .order-details {
    padding: 16px 12px;
  }

  .order-header {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .order-header h1 {
    font-size: 20px;
  }

  .total-amount {
    text-align: left;
  }

  .total-amount .amount {
    font-size: 24px;
  }

  .item-price-info {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
